import type { ActionTree, Module, MutationTree } from 'vuex'
import { DocumentService } from '@/services/Document.service'
import {
  createBaseActions,
  createBaseMutations,
  createBaseState,
} from '@/store/modules/Base.module'
import {
  Document,
  DocumentsGroupPagedResult,
  DocumentSummary,
  DocumentType,
  DownloadDocumentRequest,
} from '@/classes/generated-models'
import { IBaseModuleState } from '@/store/interfaces/IBase.module'
import { DownloadsService } from '@/services/Downloads.service'
import type { IState } from '@/store'

export interface IDocumentsState extends IBaseModuleState<Document, DocumentsGroupPagedResult> {
  selectedDocuments: Document[]
  documentSummary: DocumentSummary[]
}

const documentService = new DocumentService()
const downloadsService = new DownloadsService()

const state = (): IDocumentsState => ({
  ...createBaseState<Document, DocumentsGroupPagedResult>(documentService),
  selectedDocuments: [],
  documentSummary: [],
})

const mutations: MutationTree<IDocumentsState> = {
  ...createBaseMutations<Document, DocumentsGroupPagedResult>(),
  addSelectedDocument(state, document: Document) {
    state.selectedDocuments.push(document)
  },
  removeSelectedDocument(state, id: number) {
    const index = state.selectedDocuments.findIndex(doc => doc.id === id)
    if (index >= 0) {
      state.selectedDocuments.splice(index, 1)
    }
  },
  downloadingFinished(state) {
    state.selectedDocuments = []
  },
  setDocumentSummary(state, summary: DocumentSummary[]) {
    state.documentSummary = summary
  },
}

const actions: ActionTree<IDocumentsState, IState> = {
  ...createBaseActions<Document, DocumentsGroupPagedResult>(),

  async fetchDocumentSummary({ commit, rootState }) {
    commit('setLoading', true)

    let response = await documentService.getSummary()
    if (!rootState.Account.currentUser?.hasCertificates) {
      response = response.filter(summary => summary.documentType !== DocumentType.Certificates)
    }

    commit('setDocumentSummary', response)
    commit('setLoading', false)
  },

  async downloadSelectedDocuments({ commit, state }) {
    try {
      if (state.selectedDocuments.length > 0) {
        const selectedIds = state.selectedDocuments.map(document => document.id!)
        commit('setLoading', true)
        await downloadsService.download(selectedIds, 'zip')
        commit('downloadingFinished')
      }
    } finally {
      commit('setLoading', false)
    }
  },

  async downloadDocument({ commit }, payload: { request: DownloadDocumentRequest, fileName: string, preview: boolean }) {
    try {
      commit('setLoading', true)
      await downloadsService.download(payload.request, '', payload.fileName, payload.preview)
    } finally {
      commit('setLoading', false)
    }
  },
}

export const Documents: Module<IDocumentsState, IState> = {
  namespaced: true,
  state,
  mutations,
  actions,
}
